import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, CheckCircle2, Shield, Star, Package, UserCheck, ArrowRight, XCircle, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import CardPaymentDialog from "@/components/CardPaymentDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  open: { label: "Aberto", color: "bg-blue-500" },
  hired: { label: "Em andamento", color: "bg-orange-500" },
  delivered: { label: "Entregue - aguardando cliente", color: "bg-purple-500" },
  completed: { label: "Concluído", color: "bg-green-500" },
  cancelled: { label: "Cancelado", color: "bg-red-500" },
  disputed: { label: "Em disputa", color: "bg-amber-600" },
};

export default function MyJobsSection() {
  const { user } = useAuth();
  const [data, setData] = useState({ as_client: [], as_hired: [], as_applicant: [] });
  const [tab, setTab] = useState("as_client");
  const [rateFor, setRateFor] = useState(null);
  const [rateStars, setRateStars] = useState(5);
  const [rateComment, setRateComment] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/jobs/mine`);
      setData(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);

  const hire = async (job, applicationId) => {
    if (!window.confirm(`Contratar essa proposta bloqueia o valor da carteira em escrow. Continuar?`)) return;
    setBusy(job.job_id);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/hire`, { application_id: applicationId });
      toast.success("Freelancer contratado! Fundos em escrow.");
      load();
    } catch (e) {
      if (e.response?.status === 402) {
        const app_ = (job.applications || []).find(a => a.application_id === applicationId);
        toast.info("Saldo insuficiente — pague com cartão para concluir a contratação");
        setCardHire({ job, applicationId, amount: Number(app_?.price || 0) });
      } else toast.error(e.response?.data?.detail || "Erro");
    }
    setBusy(null);
  };
  const [cardHire, setCardHire] = useState(null);
  const finishHireAfterCard = async () => {
    const { job, applicationId } = cardHire;
    try {
      await axios.post(`${API}/jobs/${job.job_id}/hire`, { application_id: applicationId });
      toast.success("Pagamento aprovado e freelancer contratado! Fundos em escrow.");
    } catch (e) { toast.error(e.response?.data?.detail || "Pagamento ok, mas a contratação falhou. Tente contratar novamente."); }
    load();
  };
  const deliver = async (job) => {
    setBusy(job.job_id);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/deliver`);
      toast.success("Trabalho marcado como entregue!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };
  const release = async (job) => {
    if (!window.confirm(`Liberar o pagamento de R$ ${Number(job.escrow_amount || 0).toFixed(2)} para o freelancer?`)) return;
    setBusy(job.job_id);
    try {
      const r = await axios.post(`${API}/jobs/${job.job_id}/release`);
      toast.success(`Pagamento liberado: R$ ${r.data.payout.toFixed(2)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };
  const submitRating = async () => {
    setBusy(rateFor.job_id);
    try {
      await axios.post(`${API}/jobs/${rateFor.job_id}/rate`, { stars: rateStars, comment: rateComment });
      toast.success("Avaliação enviada!");
      setRateFor(null); setRateStars(5); setRateComment("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };

  const cancelJob = async (job) => {
    const reason = window.prompt("Motivo do cancelamento (opcional):", "");
    if (reason === null) return;
    if (!window.confirm("Confirma o cancelamento? O valor em escrow será reembolsado ao cliente.")) return;
    setBusy(job.job_id);
    try {
      const r = await axios.post(`${API}/jobs/${job.job_id}/cancel`, { reason: reason || "" });
      toast.success(`Cancelado. Reembolso: R$ ${r.data.refunded.toFixed(2)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };

  const disputeJob = async (job) => {
    const reason = window.prompt("Descreva o motivo da disputa (mínimo 5 caracteres):", "");
    if (!reason || reason.trim().length < 5) { toast.error("Informe um motivo válido"); return; }
    setBusy(job.job_id);
    try {
      await axios.post(`${API}/jobs/${job.job_id}/dispute`, { reason });
      toast.success("Disputa aberta. Admin será notificado.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setBusy(null);
  };

  const jobs = data[tab] || [];

  return (
    <div className="space-y-4">
      <Card className="p-3 flex flex-wrap items-center justify-between gap-2 bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/30">
        <div className="flex items-center gap-2 text-sm">
          <Shield className="w-5 h-5 text-primary" />
          <div>
            <div className="font-bold">Escrow protegido</div>
            <div className="text-[11px] text-muted-foreground">Cliente bloqueia o valor · freelancer entrega · cliente libera o pagamento · ambos se avaliam.</div>
          </div>
        </div>
      </Card>

      <div className="flex gap-2 flex-wrap" data-testid="myjobs-tabs">
        {[
          { key: "as_client", label: "Contratante", icon: Briefcase, count: data.as_client.length },
          { key: "as_hired", label: "Contratado", icon: UserCheck, count: data.as_hired.length },
          { key: "as_applicant", label: "Candidaturas", icon: Package, count: data.as_applicant.length },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`myjobs-tab-${t.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 flex items-center gap-1 ${tab === t.key ? "border-primary bg-primary/10" : "border-border"}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label} <Badge variant="secondary" className="ml-1 text-[9px]">{t.count}</Badge>
          </button>
        ))}
      </div>

      {jobs.length === 0 && (
        <Card className="p-8 text-center text-muted-foreground">
          <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-40" />
          Nenhum trabalho nessa aba.
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {jobs.map(j => {
          const st = STATUS_LABEL[j.status] || { label: j.status, color: "bg-gray-500" };
          const isClient = j.user_id === user?.user_id;
          const isFreelancer = j.hired_user_id === user?.user_id;
          const myApp = (j.applications || []).find(a => a.user_id === user?.user_id);
          return (
            <Card key={j.job_id} className="p-4 space-y-3" data-testid={`myjob-${j.job_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="font-bold">{j.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{j.description}</div>
                </div>
                <div className="text-right">
                  <Badge className={`${st.color} text-white text-[10px]`}>{st.label}</Badge>
                  <div className="text-sm font-bold text-primary mt-1">R$ {Number(j.budget || 0).toFixed(2)}</div>
                </div>
              </div>

              {/* Applications view (client) */}
              {isClient && j.status === "open" && (j.applications || []).length > 0 && (
                <div className="border-t pt-2 space-y-1" data-testid={`myjob-apps-${j.job_id}`}>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Propostas ({j.applications.length})</div>
                  {(j.applications || []).slice(0, 4).map(a => (
                    <div key={a.application_id} className="flex items-center gap-2 p-2 rounded bg-muted/40" data-testid={`myjob-app-${a.application_id}`}>
                      <Avatar className="w-7 h-7"><AvatarImage src={a.applicant_picture} /><AvatarFallback>{a.applicant_name?.[0]}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{a.applicant_name}</div>
                        <div className="text-[10px] text-muted-foreground line-clamp-1">{a.message}</div>
                      </div>
                      <div className="text-xs font-bold text-primary">R$ {Number(a.price).toFixed(2)}</div>
                      <Button size="sm" data-testid={`myjob-hire-${a.application_id}`} disabled={busy === j.job_id} onClick={() => hire(j, a.application_id)} className="rounded-full h-7 text-xs gap-1">
                        <Shield className="w-3 h-3" /> Contratar
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {isFreelancer && j.status === "hired" && (
                  <Button size="sm" disabled={busy === j.job_id} onClick={() => deliver(j)} data-testid={`myjob-deliver-${j.job_id}`} className="rounded-full h-7 text-xs gap-1">
                    <Package className="w-3 h-3" /> Marcar como entregue
                  </Button>
                )}
                {isClient && j.status === "delivered" && (
                  <Button size="sm" disabled={busy === j.job_id} onClick={() => release(j)} data-testid={`myjob-release-${j.job_id}`} className="rounded-full h-7 text-xs gap-1 bg-green-600 hover:bg-green-700">
                    <CheckCircle2 className="w-3 h-3" /> Liberar pagamento
                  </Button>
                )}
                {isClient && j.status === "hired" && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Package className="w-3 h-3" /> Aguardando o freelancer entregar
                  </div>
                )}
                {(isClient || isFreelancer) && j.status === "completed" && (
                  <Button size="sm" variant="outline" data-testid={`myjob-rate-${j.job_id}`} onClick={() => { setRateFor(j); setRateStars(5); setRateComment(""); }} className="rounded-full h-7 text-xs gap-1">
                    <Star className="w-3 h-3" /> Avaliar
                  </Button>
                )}
                {(isClient || isFreelancer) && j.status === "hired" && (
                  <Button size="sm" variant="outline" disabled={busy === j.job_id} onClick={() => cancelJob(j)} data-testid={`myjob-cancel-${j.job_id}`} className="rounded-full h-7 text-xs gap-1 text-red-600 border-red-500/40 hover:bg-red-500/10">
                    <XCircle className="w-3 h-3" /> Cancelar (reembolso)
                  </Button>
                )}
                {(isClient || isFreelancer) && (j.status === "hired" || j.status === "delivered") && !j.dispute_id && (
                  <Button size="sm" variant="ghost" disabled={busy === j.job_id} onClick={() => disputeJob(j)} data-testid={`myjob-dispute-${j.job_id}`} className="rounded-full h-7 text-xs gap-1 text-amber-600 hover:bg-amber-500/10">
                    <AlertTriangle className="w-3 h-3" /> Abrir disputa
                  </Button>
                )}
                {j.status === "disputed" && (
                  <div className="text-[11px] text-amber-600 flex items-center gap-1" data-testid={`myjob-disputed-${j.job_id}`}>
                    <AlertTriangle className="w-3 h-3" /> Em análise pelo admin
                  </div>
                )}
                {myApp && !isFreelancer && !isClient && (
                  <div className="text-[11px] text-muted-foreground">Sua proposta: R$ {Number(myApp.price).toFixed(2)} · aguardando cliente</div>
                )}
              </div>

              {j.escrow_amount > 0 && (
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Shield className="w-3 h-3 text-primary" /> Escrow: R$ {Number(j.escrow_amount).toFixed(2)}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Rating dialog */}
      <Dialog open={!!rateFor} onOpenChange={(v) => !v && setRateFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Avaliar: {rateFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-1 justify-center">
              {[1,2,3,4,5].map(n => (
                <button key={n} data-testid={`rate-star-${n}`} onClick={() => setRateStars(n)} className={`text-3xl transition ${n <= rateStars ? "text-yellow-400" : "text-muted"}`}>
                  ★
                </button>
              ))}
            </div>
            <Textarea data-testid="rate-comment" rows={3} placeholder="Comentário (opcional)" value={rateComment} onChange={(e) => setRateComment(e.target.value)} />
            <Button data-testid="rate-submit" onClick={submitRating} disabled={!!busy} className="w-full rounded-full">Enviar avaliação</Button>
          </div>
        </DialogContent>
      </Dialog>
      <CardPaymentDialog
        open={!!cardHire}
        onOpenChange={(v) => !v && setCardHire(null)}
        purpose="job_hire"
        amount={cardHire?.amount || 0}
        referenceId={cardHire?.job?.job_id}
        packageId={cardHire?.applicationId}
        title="Pagar contratação com cartão"
        description="O valor é creditado na sua carteira e bloqueado em escrow para o freelancer assim que o pagamento for aprovado."
        onSuccess={finishHireAfterCard}
      />
    </div>
  );
}
